import { useEffect, useState, useSyncExternalStore } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";

import {
  getAuthSessionSnapshot,
  subscribeAuthSession,
} from "@/lib/auth/session";

/** Identity used before login, matching the anonymous LaunchDarkly context. */
const ANONYMOUS_IDENTITY = "anonymous";

/** Stop blocking the UI when LaunchDarkly never answers (offline, blocked SDK). */
export const FLAG_EVALUATION_TIMEOUT_MS = 3000;

/**
 * Points LaunchDarkly at the signed-in user and reports whether flags have been
 * evaluated for that identity.
 *
 * The client boots anonymous and `useFlags` returns the fail-closed defaults
 * until it initializes and identifies. A remembered session renders `#/home`
 * on that first frame, so without this signal the flag reads as off and the
 * Developer Portal redirects the user back to the docs (AES-290).
 */
export function useLaunchDarklyFlagsResolved(): boolean {
  const client = useLDClient();
  const session = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const email = session?.user.email;
  const identity = email ?? ANONYMOUS_IDENTITY;
  const [evaluatedIdentity, setEvaluatedIdentity] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!client) {
      return;
    }

    let active = true;
    void client
      .identify(email ? { key: email, email } : { anonymous: true })
      .catch(() => undefined)
      .finally(() => {
        if (active) {
          setEvaluatedIdentity(identity);
        }
      });

    return () => {
      active = false;
    };
  }, [client, email, identity]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setTimedOut(true),
      FLAG_EVALUATION_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  return timedOut || (Boolean(client) && evaluatedIdentity === identity);
}
